import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  InputGroup,
  InputLeftElement,
  useToast,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
} from '@chakra-ui/react'
import { SearchIcon, ChevronDownIcon, ArrowBackIcon } from '@chakra-ui/icons'
import { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import type { Location, LocationProfileContactFormValues } from '../../../types/location'
import { formatLocationAddress } from '../../../types/location'
import type { JoinPrescriberLocationProfileRequest } from '../prescriberLocationProfilesSlice'
import { useJoinPrescriberLocationProfileMutation } from '../prescriberLocationProfilesSlice'
import { LocationProfileContactFields } from './LocationProfileContactFields'

interface JoinLocationModalProps {
  isOpen: boolean
  onClose: () => void
  availableLocations: Location[]
  hideCloseButton?: boolean
  onBack?: () => void
}

export const JoinLocationModal = ({
  isOpen,
  onClose,
  availableLocations,
  hideCloseButton,
  onBack,
}: JoinLocationModalProps) => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LocationProfileContactFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      providerNumber: '',
      phone: '',
      email: '',
    },
  })

  const [joinLocationProfile, { isLoading }] = useJoinPrescriberLocationProfileMutation()
  const toast = useToast()

  const activeAvailableLocations = useMemo(
    () => availableLocations.filter((loc) => loc.status),
    [availableLocations]
  )

  const filteredLocations = useMemo(() => {
    if (!searchQuery) return activeAvailableLocations
    return activeAvailableLocations.filter((loc) =>
      loc.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [activeAvailableLocations, searchQuery])

  useEffect(() => {
    if (isOpen) {
      setSelectedLocation(null)
      setSearchQuery('')
      reset({
        providerNumber: '',
        phone: '',
        email: '',
      })
    }
  }, [isOpen, reset])

  const handleLocationSelect = (loc: Location) => {
    setSelectedLocation(loc)
    setValue('phone', loc.phone || '', { shouldValidate: true })
    setValue('email', loc.email || '', { shouldValidate: true })
  }

  const buildJoinPayload = (
    locationId: string,
    data: LocationProfileContactFormValues
  ): JoinPrescriberLocationProfileRequest => {
    const payload: JoinPrescriberLocationProfileRequest = { locationId }
    const providerNumber = data.providerNumber.trim()
    const phone = data.phone.trim()
    const email = data.email.trim()
    if (providerNumber) payload.providerNumber = providerNumber
    if (phone) payload.phone = phone
    if (email) payload.email = email
    return payload
  }

  const handleSave = async (data: LocationProfileContactFormValues) => {
    if (!selectedLocation) return

    try {
      await joinLocationProfile(buildJoinPayload(selectedLocation.id, data)).unwrap()

      toast({
        title: 'Successfully joined location',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
      onClose()
    } catch (error: any) {
      toast({
        title: 'Failed to join location',
        description: error?.data?.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
      closeOnOverlayClick={!hideCloseButton}
      closeOnEsc={!hideCloseButton}
    >
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader
          fontSize="lg"
          fontWeight="bold"
          color="gray.900"
          pb={2}
          display="flex"
          alignItems="center"
        >
          {onBack && (
            <IconButton
              aria-label="Go back"
              icon={<ArrowBackIcon boxSize={5} />}
              variant="ghost"
              size="sm"
              mr={2}
              onClick={onBack}
            />
          )}
          <Box>
            Join a location
            {selectedLocation && (
              <Text fontSize="sm" color="blue.500" fontWeight="normal">
                {selectedLocation.name}
              </Text>
            )}
          </Box>
        </ModalHeader>
        {!hideCloseButton && <ModalCloseButton top={3} right={3} />}

        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.700">
                Location
              </FormLabel>
              <Menu matchWidth>
                <MenuButton
                  as={Button}
                  variant="outline"
                  w="full"
                  textAlign="left"
                  fontWeight="normal"
                  color={selectedLocation ? 'gray.900' : 'gray.500'}
                  borderColor="gray.200"
                  _hover={{ borderColor: 'gray.300' }}
                  _active={{ bg: 'white' }}
                  rightIcon={<ChevronDownIcon />}
                >
                  {selectedLocation ? selectedLocation.name : 'Select a location'}
                </MenuButton>
                <MenuList p={2} maxH="300px" overflowY="auto">
                  <InputGroup size="sm" mb={2}>
                    <InputLeftElement pointerEvents="none" color="gray.400">
                      <SearchIcon boxSize={3.5} />
                    </InputLeftElement>
                    <Input
                      placeholder="Search locations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </InputGroup>
                  {filteredLocations.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={2}>
                      No locations found
                    </Text>
                  ) : (
                    filteredLocations.map((loc) => (
                      <MenuItem
                        key={loc.id}
                        onClick={() => handleLocationSelect(loc)}
                        borderRadius="md"
                        _hover={{ bg: 'blue.50' }}
                        px={3}
                        py={2}
                      >
                        <VStack align="stretch" spacing={0}>
                          <Text fontWeight="bold" fontSize="sm" color="gray.900">
                            {loc.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {formatLocationAddress(loc)}
                          </Text>
                        </VStack>
                      </MenuItem>
                    ))
                  )}
                </MenuList>
              </Menu>
            </FormControl>

            {selectedLocation ? (
              <LocationProfileContactFields
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
              />
            ) : null}
          </VStack>
        </ModalBody>

        <ModalFooter pt={2} pb={6}>
          <Button variant="outline" mr={3} onClick={onClose} isDisabled={isLoading} size="md">
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit(handleSave)}
            isLoading={isLoading}
            size="md"
            px={8}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
